/**
 * UnlockModal — celebrates a newly unlocked achievement with the custom
 * badge art and share-to-social CTAs.
 *
 * If multiple achievements unlock in a single solve, they're queued and
 * cycled with a "Next →" button.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Copy, Twitter, ExternalLink, ChevronRight, Download } from 'lucide-react';
import { toast } from 'sonner';
import BadgeArt from './BadgeArt';
import { buildShareImage, downloadBlob } from '../lib/buildShareImage';
import { useAuth } from '../contexts/AuthContext';

const CATEGORY_COLORS = {
  Progress: '#34C759',
  Milestones: '#8B5A3C',
  Speed: '#FF9F0A',
  Mastery: '#AF52DE',
  Streaks: '#FF3B30',
};

const SHARE_URL = typeof window !== 'undefined'
  ? window.location.origin
  : 'https://sudokuacademy.org';

export default function UnlockModal({ achievements, onClose }) {
  const [index, setIndex] = useState(0);
  const [generating, setGenerating] = useState(false);
  const { user } = useAuth();

  // Reset the cursor whenever a new batch arrives.
  useEffect(() => {
    setIndex(0);
  }, [achievements]);

  if (!achievements || achievements.length === 0) return null;
  const a = achievements[index];
  const color = CATEGORY_COLORS[a.category] || '#8B5A3C';

  const shareText = `I just unlocked ${a.name} on Sudoku Academy — ${a.description}`;
  const username = user?.username || user?.email?.split('@')[0] || 'anonymous';

  const generateCard = async () => {
    const blob = await buildShareImage({
      achievementId: a.id,
      name: a.name,
      description: a.description,
      category: a.category,
      username,
    });
    return blob;
  };

  const handleShareX = async () => {
    setGenerating(true);
    try {
      const blob = await generateCard();
      downloadBlob(blob, `sudoku-academy-${a.id}.png`);
      toast.success('Image saved — attach it to your tweet!');
      // Small delay so the download actually initiates before popup is opened.
      setTimeout(() => {
        const url = new URL('https://twitter.com/intent/tweet');
        url.searchParams.set('text', shareText);
        url.searchParams.set('url', SHARE_URL);
        window.open(url.toString(), '_blank', 'noopener,noreferrer');
      }, 300);
    } catch (err) {
      toast.error('Could not build share image — please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    setGenerating(true);
    try {
      const blob = await generateCard();
      downloadBlob(blob, `sudoku-academy-${a.id}.png`);
      toast.success('Saved to your downloads.');
    } catch (err) {
      toast.error('Could not build share image — please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n\n${SHARE_URL}`);
      toast.success('Copied! Paste it anywhere.');
    } catch {
      toast.error('Copy failed — your browser blocked clipboard access.');
    }
  };

  const handleDiscord = () => {
    // Opens Discord invite so the user can paste into their server manually.
    const invite = process.env.REACT_APP_DISCORD_INVITE_URL;
    if (invite) {
      window.open(invite, '_blank', 'noopener,noreferrer');
    } else {
      handleCopy();
    }
  };

  const hasNext = index < achievements.length - 1;
  const goNext = () => setIndex((i) => i + 1);

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="max-w-md bg-[var(--background-surface)] border-[var(--border-subtle)]"
        data-testid="unlock-modal"
      >
        <DialogHeader>
          <DialogTitle
            className="text-center font-heading text-xl"
            style={{ color }}
          >
            🏆 Achievement unlocked!
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 py-4">
          <div
            className="relative"
            style={{ filter: `drop-shadow(0 4px 12px ${color}66)` }}
          >
            <BadgeArt
              achievementId={a.id}
              category={a.category}
              locked={false}
              size={128}
            />
          </div>

          <div className="text-center">
            <h3 className="font-heading text-lg font-bold text-[var(--text-primary)]">
              {a.name}
            </h3>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              {a.description}
            </p>
            {achievements.length > 1 && (
              <p className="text-xs text-[var(--text-disabled)] font-mono mt-2">
                {index + 1} of {achievements.length}
              </p>
            )}
          </div>

          <div className="w-full grid grid-cols-4 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleShareX}
              disabled={generating}
              className="border-[var(--border-subtle)]"
              data-testid="unlock-share-x"
            >
              <Twitter className="w-4 h-4 mr-1" />
              X
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              disabled={generating}
              className="border-[var(--border-subtle)]"
              data-testid="unlock-share-download"
            >
              <Download className="w-4 h-4 mr-1" />
              PNG
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              className="border-[var(--border-subtle)]"
              data-testid="unlock-share-copy"
            >
              <Copy className="w-4 h-4 mr-1" />
              Copy
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDiscord}
              className="border-[var(--border-subtle)]"
              data-testid="unlock-share-discord"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Discord
            </Button>
          </div>

          {hasNext ? (
            <Button
              onClick={goNext}
              className="w-full"
              style={{ backgroundColor: color }}
              data-testid="unlock-next-btn"
            >
              Next achievement
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full border-[var(--border-subtle)]"
              data-testid="unlock-close-btn"
            >
              Keep playing
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
